import { Controller, type UseFormReturn } from "react-hook-form";
import { type CompleteRegistrationFormValues } from "~/features/registration/types/CompleteRegistrationFormValues";
import { Row } from "~/common/components/Layout";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";
import { useMemo } from "react";

import Parameter from "~/common/components/Parameter";
import PasswordInput from "~/common/components/materialInput/PasswordInput";
import {
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  PASSWORD_REGEXP,
  PHONE_REGEXP,
  ZIPCODE_MAX_LENGTH,
  ZIPCODE_MIN_LENGTH,
  ZIPCODE_REGEXP,
} from "~/common/utils/validations";
import { SimpleAutocomplete } from "~/common/components/Select/SimpleAutocompleteSelect";
import { withClass } from "~/common/hoc/withClass";
import { FormSection } from "~/pages/MyAccountPage/MyAccountPage";
import { AffiliateFormSection } from "~/features/affiliate/components/AffiliateFormSection";
import { type AffiliatePartnerData } from "~/domains/affiliate/types/models";
import { type DropdownEntry } from "~/common/types";
import { useTranslation } from "react-i18next";

import { isNonNull } from "~/common/utils/reducers";
import { TrainingConsent, FlagBadge } from "~/features/myAccount/profile/components/TrainingConsent";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";

import { makeSelectRef4RHF } from "~/features/myAccount/profile/formUtils";
import { Alert } from "~/common/mui/Alert/Alert";
import { Box, Stack, TextField, styled } from "~/common/mui";

interface Props extends UseFormReturn<CompleteRegistrationFormValues> {
  email: string;
  initialCustomerCategory?: string;
  titleOptions: DropdownEntry<string>[];
  salutationOptions: DropdownEntry<string>[];
  countryOptions: DropdownEntry<string>[];
  industryOptions: DropdownEntry<string>[];
  jobTitleOptions: DropdownEntry<string>[];
  functionalAreaOptions: DropdownEntry<string>[];
  salesPartner?: AffiliatePartnerData;
  isEnableFieldsTraining: boolean;
  customerCategoryOptions: DropdownEntry<string>[];
  knowledgeAreaOptions: DropdownEntry<string>[];
  languageOptions: DropdownEntry<string>[];
}

export const CompleteRegistrationForm = ({
  email,
  initialCustomerCategory,
  titleOptions,
  salutationOptions,
  countryOptions,
  industryOptions,
  jobTitleOptions,
  functionalAreaOptions,
  salesPartner,
  register,
  control,
  watch,
  getValues,
  trigger,
  setValue,
  customerCategoryOptions,
  knowledgeAreaOptions,
  languageOptions,
  isEnableFieldsTraining,
}: Props) => {
  const { t } = useTranslation();
  const getLabel = (name: string, required?: boolean) => t(`page.completeRegistration.${name}`) + (required ? "*" : "");
  const customerCategory = watch("customerCategory");

  const pleaseEnterError = (fieldName: string) => t("page.profile.pleaseEnter." + fieldName);

  const affiliates = useMemo(() => [salesPartner].filter(isNonNull), [salesPartner]);

  // after clean up move to CompleteRegistrationPage.tsx line 85 (formProps defaultValues)
  if (initialCustomerCategory && customerCategory !== initialCustomerCategory) {
    setValue("customerCategory", initialCustomerCategory);
  }

  const isMinDesktop = useIsMinDesktopSize();

  return (
    <>
      <Row>
        <Column>
          <FormSection title={t("myAccount.name.title")}>
            <Controller
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                <SimpleSelect
                  values={titleOptions}
                  label={getLabel("customerTitle", false)}
                  error={!!error}
                  helperText={error?.message}
                  fullWidth={true}
                  data-test-id={"complete-registration-title"}
                  onBlur={onBlur}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                />
              )}
              name={"title"}
              control={control}
            />
            <Controller
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                <SimpleSelect
                  values={salutationOptions}
                  label={getLabel("salutation", true)}
                  error={!!error}
                  helperText={error?.message}
                  fullWidth={true}
                  data-test-id={"complete-registration-salutation"}
                  onBlur={onBlur}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                />
              )}
              name={"salutation"}
              control={control}
              rules={{ required: pleaseEnterError("salutation") }}
            />

            <Controller
              control={control}
              name="firstName"
              rules={{ required: pleaseEnterError("firstName") }}
              render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                <TextField
                  fullWidth
                  label={getLabel("firstName", true)}
                  inputRef={ref}
                  helperText={error?.message ?? " "}
                  error={!!error}
                  inputProps={{ "data-test-id": "complete-registration-firstName" }}
                  autoComplete={"given-name"}
                  {...inputProps}
                />
              )}
            />

            <Controller
              control={control}
              name="lastName"
              rules={{ required: pleaseEnterError("lastName") }}
              render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                <TextField
                  fullWidth
                  label={getLabel("lastName", true)}
                  inputProps={{ "data-test-id": "complete-registration-lastName" }}
                  inputRef={ref}
                  helperText={error?.message ?? " "}
                  error={!!error}
                  autoComplete={"last-name"}
                  {...inputProps}
                />
              )}
            />

            <Parameter label={getLabel("email")} value={<div className="title-medium">{email}</div>} />
            <Box sx={{ mt: "70px" }}>
            <FormSection title={t("page.completeRegistration.password")}>
              <Controller
                name={"password"}
                control={control}
                rules={{
                  // required: {value: true, message: t("page.completeRegistration.errorMatchRequirements")},
                  required: { value: true, message: pleaseEnterError("password") },
                  minLength: {
                    value: PASSWORD_MIN_LENGTH,
                    message: t("page.completeRegistration.errorMatchRequirements"),
                  },
                  maxLength: {
                    value: PASSWORD_MAX_LENGTH,
                    message: t("page.completeRegistration.errorMatchRequirements"),
                  },
                  pattern: { value: PASSWORD_REGEXP, message: t("page.completeRegistration.errorMatchRequirements") },
                }}
                render={({ field: { ref, onBlur, ...inputProps }, fieldState: { error } }) => (
                  <PasswordInput
                    label={getLabel("password", true)}
                    error={!!error}
                    helperLabel={error?.message}
                    data-test-id={"complete-registration-password"}
                    onBlur={() => {
                      onBlur();
                      trigger(["password", "confirmPassword"]);
                    }}
                    autoComplete={"new-password"}
                    inputRef={ref}
                    {...inputProps}
                  />
                )}
              />

              <Controller
                name={"confirmPassword"}
                control={control}
                rules={{
                  required: { value: true, message: pleaseEnterError("password") },
                  validate: (val) =>
                    val === getValues().password || (t("page.completeRegistration.errorTwoPasswordsMatch")! as string),
                }}
                render={({ field: { ref, onBlur, ...inputProps }, fieldState: { error } }) => (
                  <PasswordInput
                    label={getLabel("confirmPassword", true)}
                    error={!!error}
                    helperLabel={error?.message}
                    data-test-id={"complete-registration-confirmPassword"}
                    onBlur={() => {
                      onBlur();
                      trigger(["password", "confirmPassword"]);
                    }}
                    autoComplete={"new-password"}
                    inputRef={ref}
                    {...inputProps}
                  />
                )}
              />

              <Alert severity={"info"}>{t("page.completeRegistration.passwordInfo")}</Alert>
            </FormSection>
            </Box>

          </FormSection>

          {salesPartner && (
            <AffiliateFormSection
              control={control}
              register={register}
              setValue={setValue}
              watch={watch}
              affiliates={affiliates}
            />
          )}
        </Column>

        <Column>
          <FormSection title={t("myAccount.company.title")}>
            <Controller
              name={"company"}
              control={control}
              rules={{ required: { value: true, message: pleaseEnterError("companyName") } }}
              render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                <TextField
                  fullWidth
                  label={getLabel("companyName", true)}
                  inputProps={{ "data-test-id": "complete-registration-company" }}
                  autoComplete={"organization"}
                  error={!!error}
                  helperText={error?.message ?? " "}
                  inputRef={ref}
                  {...inputProps}
                />
              )}
            />

            <Controller
              render={({ field: { ref, value, onChange, onBlur }, fieldState: { error } }) => (
                <SimpleSelect
                  values={industryOptions}
                  label={getLabel("industrySector", true)}
                  fullWidth={true}
                  data-test-id={"complete-registration-industry"}
                  onBlur={onBlur}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  error={!!error}
                  helperText={error?.message ?? ""}
                  ref={makeSelectRef4RHF(ref)}
                />
              )}
              name={"industrySector"}
              control={control}
              rules={{ required: pleaseEnterError("industrySector") }}
            />

            <Controller
              render={({ field: { value, onChange, onBlur } }) => (
                <SimpleSelect
                  values={functionalAreaOptions}
                  label={getLabel("functionalArea")}
                  fullWidth={true}
                  data-test-id={"complete-registration-functionalArea"}
                  onBlur={onBlur}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                />
              )}
              name={"functionalArea"}
              control={control}
            />

            {isEnableFieldsTraining && (
              <Alert severity={"info"} className={"mb-3"}>
                {t("page.completeRegistration.contactInfo")}
              </Alert>
            )}

            <Controller
              name={"jobTitle"}
              control={control}
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                <SimpleSelect
                  values={jobTitleOptions}
                  label={getLabel("jobTitle", false)}
                  fullWidth={true}
                  data-test-id={"complete-registration-jobTitle"}
                  onBlur={onBlur}
                  value={value}
                  error={!!error}
                  onChange={(e) => onChange(e.target.value)}
                  helperText=""
                />
              )}
            />

            <Controller
              name={"address"}
              control={control}
              rules={{
                required: isEnableFieldsTraining,
              }}
              render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                <TextField
                  fullWidth
                  label={getLabel("address", isEnableFieldsTraining)}
                  inputProps={{ "data-test-id": "complete-registration-address" }}
                  autoComplete={"address-line1"}
                  error={!!error}
                  inputRef={ref}
                  helperText=" "
                  {...inputProps}
                />
              )}
            />

            <Controller
              name={"addressAdditional"}
              control={control}
              render={({ field: { ref, ...inputProps } }) => (
                <TextField
                  fullWidth
                  label={getLabel("addressAdditional")}
                  inputProps={{ "data-test-id": "complete-registration-addressAdditional" }}
                  autoComplete={"address-line2"}
                  inputRef={ref}
                  {...inputProps}
                />
              )}
            />

            <ZipCityField>
              <Controller
                name={"zipCode"}
                control={control}
                rules={{
                  minLength: {
                    value: ZIPCODE_MIN_LENGTH,
                    message: t("checkoutPage.addressModal.error.isTooShortPart", {
                      fieldname: t(`checkoutPage.addressModal.input.label.zipCode`),
                    }),
                  },
                  maxLength: {
                    value: ZIPCODE_MAX_LENGTH,
                    message: t("checkoutPage.addressModal.error.isTooLongPart", {
                      fieldname: t(`checkoutPage.addressModal.input.label.zipCode`),
                    }),
                  },
                  pattern: {
                    value: ZIPCODE_REGEXP,
                    message: t("checkoutPage.addressModal.error.forbidenCharactersPart", {
                      fieldname: t(`checkoutPage.addressModal.input.label.zipCode`),
                    }),
                  },
                  required: isEnableFieldsTraining,
                }}
                render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                  <TextField
                    fullWidth
                    label={getLabel("zipCode", isEnableFieldsTraining)}
                    error={!!error}
                    helperText={error?.message}
                    inputProps={{
                      "data-test-id": "complete-registration-zipCode",
                      onBeforeInput: (e: any) => {
                        // @ts-ignore
                        const val = e.target.value + e.data;
                        if (!ZIPCODE_REGEXP.test(val.trim())) {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      },
                    }}
                    autoComplete={"postal-code"}
                    inputRef={ref}
                    {...inputProps}
                  />
                )}
              />

              <Controller
                name={"city"}
                control={control}
                rules={{
                  required: isEnableFieldsTraining,
                }}
                render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                  <TextField
                    fullWidth
                    label={getLabel("city", isEnableFieldsTraining)}
                    inputProps={{ "data-test-id": "complete-registration-city" }}
                    autoComplete={"city"}
                    inputRef={ref}
                    error={!!error}
                    {...inputProps}
                  />
                )}
              />
            </ZipCityField>

            <Controller
              name={"country"}
              control={control}
              rules={{ required: { value: true, message: t("validation.country.missing") } }}
              defaultValue={""}
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                <SimpleAutocomplete
                  label={t("STCfinder.table.header.country") + "*"}
                  data-test-id={"complete-registration-country"}
                  values={countryOptions}
                  fullWidth={true}
                  error={!!error}
                  helperText={error?.message ?? " "}
                  onBlur={onBlur}
                  value={value}
                  onChange={(value) => onChange(value)}
                />
              )}
            />

            <Controller
              name={"phone"}
              control={control}
              render={({ field: { ref, ...inputProps } }) => (
                <TextField
                  fullWidth
                  label={getLabel("phone")}
                  helperText={getLabel("examplePhone")}
                  inputProps={{
                    "data-test-id": "complete-registration-phone",
                    onBeforeInput: (e: any) => {
                      // @ts-ignore
                      const val = e.target.value + e.data;
                      if (!PHONE_REGEXP.test(val.trim())) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    },
                  }}
                  autoComplete={"tel"}
                  inputRef={ref}
                  {...inputProps}
                />
              )}
            />

            {isEnableFieldsTraining && (
              <Controller
                name={"vat"}
                control={control}
                render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                  <TextField
                    fullWidth
                    label={getLabel("vat")}
                    helperText={getLabel("exampleVat")}
                    inputProps={{ "data-test-id": "complete-registration-vat" }}
                    error={!!error}
                    inputRef={ref}
                    {...inputProps}
                  />
                )}
              />
            )}

            <Alert severity={"info"}>{t("page.completeRegistration.contactInfo")}</Alert>
          </FormSection>
        </Column>

        <Column>
          {isMinDesktop && (
            <div className="d-flex">
              <TrainingTitle className="mr-16">
                {t("page.registerForFree.baner.trainingCampus")}
              </TrainingTitle>
              <FlagBadge>{t("myAccount.training.flag.label")}</FlagBadge>
            </div>
          )}
          <TrainingConsent
            checked={isEnableFieldsTraining}
            disabled={true}
            className={"scha__complete-registration__training"}
          />

          {isEnableFieldsTraining && (
            <>
              <p className="my-30 title-medium">{t("myAccount.training.welcome")}</p>
              <Stack gap={2}>
                <Controller
                  rules={{
                    required: isEnableFieldsTraining,
                  }}
                  render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                    <SimpleSelect
                      values={languageOptions}
                      label={getLabel("language", isEnableFieldsTraining)}
                      fullWidth={true}
                      error={!!error}
                      data-test-id={"complete-registration-language"}
                      onBlur={onBlur}
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                    />
                  )}
                  name={"language"}
                  control={control}
                />

                <Controller
                  rules={{
                    required: isEnableFieldsTraining,
                  }}
                  render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                    <SimpleSelect
                      values={customerCategoryOptions}
                      label={getLabel("customerCategory", isEnableFieldsTraining)}
                      fullWidth={true}
                      error={!!error}
                      data-test-id={"complete-registration-customer-category"}
                      onBlur={onBlur}
                      value={value}
                      disabled={!!initialCustomerCategory}
                      onChange={(e) => onChange(e.target.value)}
                    />
                  )}
                  name={"customerCategory"}
                  control={control}
                />
                {!initialCustomerCategory && (
                  <Alert severity={"info"}>{t("page.completeRegistration.categoryInfo")}</Alert>
                )}

                <Controller
                  rules={{
                    required: isEnableFieldsTraining,
                  }}
                  render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                    <SimpleSelect
                      values={knowledgeAreaOptions}
                      label={getLabel("knowledgeArea", isEnableFieldsTraining)}
                      fullWidth={true}
                      error={!!error}
                      data-test-id={"complete-registration-knowledge-area"}
                      onBlur={onBlur}
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                    />
                  )}
                  name={"knowledgeArea"}
                  control={control}
                />
              </Stack>
            </>
          )}
        </Column>
      </Row>

      <div className={"mb-30"}>{t("page.completeRegistration.mandatory")}</div>
    </>
  );
};

const ZipCityField = styled("div")(({ theme }) => ({
  display: "flex",
  "& > *:nth-child(1)": {
    width: "31%",
    marginRight: theme.spacing(2),
  },
  "& > *:nth-child(2)": {
    width: "69%",
    marginLeft: theme.spacing(2),
  },
}));

const TrainingTitle = styled("h3")(({ theme }) => ({
  ...theme.typography.titleLarge,
  marginTop: 0,
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("tablet")]: {
    ...theme.typography.headlineSmall,
    marginBottom: theme.spacing(3),
  },
}));

const Column = withClass("col-md-4 col-sm-12")("div");
